import React, { useState, Fragment, useEffect, useMemo, useCallback } from 'react';

import { getTutorials } from '../../../store/tutorials';
import { getCoverData, initialFVICoverData } from '../../../store/coverData';
import { getReportDetails, setItemsPerPage, setLastPhotoNumber } from '../../../store/reportDetails';
import { useAppDispatch, useAppSelector } from '../../../store/configureStore';

import { DEFAULT_REPORT_TITLE, TABLE_ITEM_COUNT_LIMITS, tutorialConstants } from '../../../utils/constants';
import { Column, PDFImageProps, PIDData, User } from '../../../utils/models';
import { getCurrentUser } from '../../../services/authService';
import { matchPhrases, reArrangePhotoRecord, validateWorkOrderNumber } from '../../../utils/utilities';
import { HardnessItem, HardnessItemReading } from '../../forms/custom-hardness/hardnessResultSummaryForm';
import { HardnessEquipment } from '../../forms/custom-hardness/hardnessEquipmentForm';

import Button from '../../themed/button';
import Conditional from '../../common/conditional';
import CustomCoverForm from '../../forms/custom-fvi/coverForm';
import CustomHardnessReport from '.';
import Drawing from '../../forms/custom-lpt/drawing';
import Elevation from '../../themed/instruction-modals/elevation';
import ExecutiveSummaryForm from '../../forms/visual-inspection/executiveSummaryForm';
import FVICoverData from '../../themed/fvi-cover-data';
import FVIStructurePIDImageForm from '../../forms/custom-fvi/fviStructurePIDImageForm';
import LoadingBadge from '../../themed/loading-badge';
import Modal from '../../themed/modal';
import HardnessRemarkForm from '../../forms/custom-hardness/hardnessRemark';
import HardnessEquipmentItems from './hardnessEquipmentItems';
import HardnessResultPreview from './hardnessResultPreview';
import MUTPhotograpicRecord from '../../forms/mut/mutPhotographicRecord';
import PDFTablePage from '../../themed/pdf/pdfTablePage';
import ReportTitleForm from '../../forms/custom-fvi/reportTitleForm';
import SyncTime from '../../blocks/sync-time';
import WarningNotice from '../custom-visual-inspection/warning-notice';
import Zippy from '../../themed/zippy';

import useAutoSaveReport from '../../../hooks/useAutoSave';
import useInspectedItemIDUpdate from '../../../hooks/useInspectedItemIDUpdate';
import useProjectDetails from '../../../hooks/useProjectDetails';

import config from '../../../utils/config';
import toastService from '../../../services/toastService';

const CreateCustomHardnessReport: React.FC = () => {
	const { fviCoverData } = useAppSelector(getCoverData);
	const { isSaving } = useAppSelector(getReportDetails);

	const [previewReport, setPreviewReport] = useState<boolean>(false);
	const [reportTitle, setReportTitle] = useState(DEFAULT_REPORT_TITLE);
	const [coverData, setCoverData] = useState(initialFVICoverData);
	const [executiveSummary, setExecutiveSummary] = useState('');
	
	const [pidData, setPIDData] = useState<PIDData>({ hasPID: 'yes', pIDNumber: '', pIDImages: [] });
	const [sketch, setSketch] = useState<PDFImageProps[]>([]);
	const [remark, setRemark]= useState('');
	const [hardnessEquipmentItems, setHardnessEquipmentItems] = useState<HardnessEquipment[]>(defaultHardnessItem);
	const [hardnessResultItems, setHardnessResultItems] = useState<HardnessItem[]>([]);
	const [photographicData, setPhotographicData] = useState<PDFImageProps[]>([]);

	const { projectDetails, error } = useProjectDetails();
	const { activeTutorial } = useAppSelector(getTutorials);
	const { role } = getCurrentUser() as User;

	const isEquipmentValid = useMemo(() => {
		return hardnessEquipmentItems.every((item) => Object.values(item).every((value) => value));
	}, [hardnessEquipmentItems]);

	const hardnessResultWithSerialNumber = useMemo(() => {
		return hardnessResultItems.map((datum, index) => ({ ...datum, id: index + 1 }));
	}, [hardnessResultItems]);

	const reportDetails = useMemo(() => {
		return {
			reportTitle,
			coverData,
			executiveSummary,
			pidData,
			sketch,
			remark,
			hardnessEquipmentItems,
			hardnessResultItems: hardnessResultWithSerialNumber,
			photographicData,
			projectDetails,
		};
	}, [projectDetails, coverData, executiveSummary, pidData, sketch, hardnessResultWithSerialNumber, hardnessEquipmentItems, remark, photographicData, reportTitle]);

	const autoSave = useAutoSaveReport(reportDetails);
	const dispatch = useAppDispatch();

	const validationMessage = useMemo(() => {
		if (!validateWorkOrderNumber(coverData.workOrderNumber)) {
			return 'Enter a valid work order number!';
		}

		if (!executiveSummary) {
			return 'Executive summary missing!';
		}

		if (matchPhrases(pidData.hasPID, 'yes') && pidData.pIDImages.length === 0) {
			return 'Provide a valid P & ID drawing';
		}

		if (hardnessEquipmentItems.length === 0) {
			return 'Please provide at least one equipment item';
		}
		
		if (hardnessResultItems.length === 0) {
			return 'Please provide at least one result item';
		}

		if (sketch.length === 0) {
			return 'Please upload Inspection Sketch!';
		}
		
		if (photographicData.length === 0) {
			return 'Please upload photographic report!';
		}

		return '';
	}, [coverData, executiveSummary, pidData, hardnessResultItems, hardnessEquipmentItems, photographicData, sketch]);

	const togglePreviewReport = useCallback((): void => {
		if (config.enableReportCompletionBeforePublish) {
			if (validationMessage) return toastService.error(validationMessage);
		}
		
		setPreviewReport((p: boolean) => !p);
	}, [validationMessage]);

	const handleSaveAndPreview = useCallback(() => {
		autoSave.saveReport();
		togglePreviewReport();
	}, [autoSave, togglePreviewReport]);
	
	useEffect(() => {
		function initializeCache() {
			const cachedReport = autoSave.oldReportData as typeof reportDetails | null;
			
			if (!cachedReport) {
				if (fviCoverData.coverData.length > 0) setCoverData(fviCoverData);
				
				return;
			}

			setReportTitle(cachedReport.reportTitle);
			setCoverData(cachedReport.coverData?.coverData?.length > 0 ? cachedReport.coverData : fviCoverData);
			setExecutiveSummary(cachedReport.executiveSummary);
			setPIDData(cachedReport.pidData);
			setSketch(cachedReport.sketch);
			setRemark(cachedReport.remark);
			setHardnessEquipmentItems(cachedReport.hardnessEquipmentItems);
			setHardnessResultItems(cachedReport.hardnessResultItems);
			setPhotographicData(reArrangePhotoRecord(cachedReport.photographicData));

			dispatch(setLastPhotoNumber(cachedReport.photographicData.length));
		}

		initializeCache();
	}, [autoSave.oldReportData, dispatch, fviCoverData]);

	useEffect(() => {
		dispatch(setItemsPerPage(TABLE_ITEM_COUNT_LIMITS.PMI));
	}, [dispatch]);

	useInspectedItemIDUpdate(hardnessEquipmentItems.map((item) => item.equipmentName));

	return (
		<Fragment>
			<Modal isVisible={previewReport} onClose={togglePreviewReport}>
				<CustomHardnessReport {...reportDetails} />
			</Modal>

			<div className="mb-4">
				<SyncTime lastSynced={autoSave.lastSaved} />
			</div>

			{projectDetails && (
				<FVICoverData
					coverDataId={config.coverDataTemplates.hardnessInspectionReport}
					isVisible={projectDetails.inspectionTypeId === config.supportedTemplates.hardnessInspectionReport && role.approveReports}
					subtitle={`Update the Cover Data information. This is the default data used on all ${projectDetails.organisationName} ${projectDetails.inspectionTypeName} Reports`}
				/>
			)}

			<Conditional isVisible={validationMessage.length > 0}>
				<WarningNotice title="Report Incomplete" body={validationMessage} />
			</Conditional>

			<Zippy headerTitle="Report Title" isValid={reportTitle.length > 0}>
				<ReportTitleForm projectTitle={projectDetails?.projectTitle} onAdd={setReportTitle} />
			</Zippy>

			<Zippy headerTitle="Cover Sheet" isValid={coverData.coverData.length > 0}>
				<CustomCoverForm initialData={coverData} onAddCoverForm={setCoverData} />
			</Zippy>
			
			<Zippy headerTitle="Executive Summary" isValid={executiveSummary.length > 0}>
				<ExecutiveSummaryForm onProceed={setExecutiveSummary} initialSummary={executiveSummary} />
			</Zippy>

			<Zippy headerTitle="P & ID" isValid={matchPhrases(pidData.hasPID, 'yes') ? pidData.pIDImages.length > 0 : true}>
				<FVIStructurePIDImageForm initialPIDData={pidData} onAdd={setPIDData} />
			</Zippy>

			<Zippy headerTitle="Inspection Sketch" isValid={sketch.length > 0}>
				<Drawing initialDrawing={sketch} onAdd={setSketch} />
			</Zippy>

			<Zippy headerTitle="General Information" isValid={isEquipmentValid}>
				<HardnessEquipmentItems hardnessEquipmentItems={hardnessEquipmentItems} onChangeHardnessEquipmentItems={setHardnessEquipmentItems} />
			</Zippy>

			<Zippy headerTitle="Result" isValid={hardnessResultItems.length > 0}>
				<HardnessResultPreview data={hardnessResultWithSerialNumber} onDataChange={setHardnessResultItems} />
				
				<div className="bg-light p-2 rounded border">
					<HardnessRemarkForm initialRemark={remark} onSave={setRemark} />
				</div>
			</Zippy>

			<Zippy headerTitle="Photographic Report" isValid={photographicData.length > 0}>
				<MUTPhotograpicRecord initialImages={photographicData} onProceed={setPhotographicData} />
			</Zippy>

			<div className="d-flex justify-content-between align-items-center">
				<div>
					<Conditional isVisible={autoSave.isProcessing}>
						<LoadingBadge message="Saving" />
					</Conditional>
				</div>

				<Elevation isElevated={activeTutorial === tutorialConstants.PREVIEW_REPORT}>
					<Button
						label="Save and Preview Report"
						disabled={error || autoSave.isProcessing || isSaving}
						onClick={handleSaveAndPreview}
					/>
				</Elevation>
			</div>
		</Fragment>
	);
};

export const defaultHardnessItem: HardnessEquipment[] = [
	{
		id: 1,
		equipmentName: '',
		modal: '',
		equipmentSerialNumber: '',
		calibrationDueDate: new Date(),
		calibrationSerialNumber: '',
		material: '',
		surfaceCondition: '',
		stageOfInspection: '',
		testMethod: '',
		loadApplied: 0,
	}
];

export const hardnessResultColumns: Column[] = [
    {
        path: 'id',
        label: 'Sr. No',
    },
    {
        path: 'item',
        label: 'ITEM',
    },
    {
        path: 'location',
        label: 'LOCATION OF TEST',
    },
    {
        path: 'readings',
        label: 'READINGS',
        style: () => ({ padding: '0.1rem' }),
        content: (item: HardnessItem) => {
			const columns: Column[] = item.readings.map((reading) => ({
				path: 'key',
				label: reading.key,
				content: (readingItem) => readingItem.find((readingMatch: HardnessItemReading) => readingMatch.key === reading.key).value
			}))

            return <PDFTablePage data={[item.readings]} columns={columns} />;
        },
    },
    {
        path: 'minimum',
        label: 'MINIMUM',
        content: (item: HardnessItem) => <span>{Math.min(...item.readings.map((reading) => reading.value))}</span>,
    },
    {
        path: 'maximum',
        label: 'MAXIMUM',
        content: (item: HardnessItem) => <span>{Math.max(...item.readings.map((reading) => reading.value))}</span>,
    },
    {
        path: 'average',
        label: 'AVERAGE',
        content: (item: HardnessItem) => <span>{(item.readings.reduce((a, b) => a + b.value, 0) / item.readings.length).toFixed(2)}</span>,
    },
    {
        path: 'result',
        label: 'RESULT',
    },
];

export default CreateCustomHardnessReport;
